import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Paper, Button, TextField,
  CircularProgress, Checkbox, FormControlLabel, FormGroup,
  MenuItem, Autocomplete,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bgvApi } from '../../../api/bgvApi';
import { employeeApi } from '../../../api/employeeApi';
import { BgvVerificationTypeDto, BgvVendorDto } from '../../../types/bgvTypes';
import { Employee } from '../../../types';

const BgvCreateVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [verificationTypes, setVerificationTypes] = useState<BgvVerificationTypeDto[]>([]);
  const [vendors, setVendors] = useState<BgvVendorDto[]>([]);

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedVtIds, setSelectedVtIds] = useState<string[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [employeesResult, verificationTypesResult, vendorsResult] = await Promise.allSettled([
        employeeApi.getAll(),
        bgvApi.getVerificationTypes(),
        bgvApi.getVendors(),
      ]);

      if (employeesResult.status === 'fulfilled') {
        setEmployees(employeesResult.value);
      } else {
        setEmployees([]);
        toast.error('Failed to load employees');
      }

      if (verificationTypesResult.status === 'fulfilled') {
        setVerificationTypes(verificationTypesResult.value.filter((verificationType) => verificationType.isActive));
      } else {
        setVerificationTypes([]);
        toast.error('Failed to load verification types');
      }

      if (vendorsResult.status === 'fulfilled') {
        setVendors(vendorsResult.value.filter((vendor) => vendor.isActive));
      } else {
        setVendors([]);
        toast.warning('Vendor list is currently unavailable. You can still create a case without assigning a vendor.');
      }

      setLoading(false);
    };
    void loadData();
  }, []);

  const handleVtToggle = (vtId: string) => {
    setSelectedVtIds((prev) =>
      prev.includes(vtId)
        ? prev.filter((id) => id !== vtId)
        : [...prev, vtId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      toast.warning('Please select an employee');
      return;
    }
    if (selectedVtIds.length === 0) {
      toast.warning('Please select at least one verification type');
      return;
    }

    setSaving(true);
    try {
      await bgvApi.createCase({
        employeeId: selectedEmployee.id,
        vendorId: selectedVendorId || undefined,
        remarks: remarks || undefined,
        verificationTypeIds: selectedVtIds,
      });
      toast.success('Verification case created successfully');
      navigate('/bgv/cases');
    } catch {
      toast.error('Failed to create verification case');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/bgv/cases')}>Back</Button>
        <Typography variant="h5" fontWeight={700}>Create New Verification</Typography>
      </Stack>

      <Paper sx={{ p: 3, maxWidth: 700 }}>
        <Stack spacing={3}>
          {/* Employee Selection */}
          <Autocomplete
            options={employees}
            getOptionLabel={(opt) => `${opt.fullName} (${opt.employeeCode})`}
            value={selectedEmployee}
            onChange={(_, val) => setSelectedEmployee(val)}
            renderInput={(params) => (
              <TextField {...params} label="Select Employee" required />
            )}
          />

          {/* Verification Types */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Verification Types *
            </Typography>
            <FormGroup>
              {verificationTypes.map((vt) => (
                <FormControlLabel
                  key={vt.id}
                  control={
                    <Checkbox
                      checked={selectedVtIds.includes(vt.id)}
                      onChange={() => handleVtToggle(vt.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{vt.name}</Typography>
                      {vt.description && (
                        <Typography variant="caption" color="text.secondary">{vt.description}</Typography>
                      )}
                    </Box>
                  }
                />
              ))}
              {verificationTypes.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  No verification types configured. Add them in Settings first.
                </Typography>
              )}
            </FormGroup>
          </Box>

          {/* Vendor Selection */}
          <TextField
            select
            label="Assign Vendor (Optional)"
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
            ))}
          </TextField>

          {/* Remarks */}
          <TextField
            label="Remarks"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
          />

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/bgv/cases')} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || !selectedEmployee || selectedVtIds.length === 0}
              sx={{textTransform:'none'}}
            >
              {saving ? <CircularProgress size={24} /> : 'Create Verification'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default BgvCreateVerificationPage;
